// KONTRAK lintas-layar (pemilik: kelompok "data"). v1: A.storeForm(store, onSaved) di public/js/admin.js.
// Dipakai: SPO Baru (tambah toko baru dari lapangan) & layar Toko.
import { useCallback } from 'react';
import { useSession } from '../../app/session.js';
import { ChoiceSelect } from '../../components/Choice.js';
import { Icon } from '../../components/Icon.js';
import { useConfirm, useModal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { api } from '../../lib/api.js';
import { getPosition } from '../../lib/device.js';
import { fv } from '../../lib/form.js';

export interface StoreSaved {
  id: number;
  name: string;
  [k: string]: unknown;
}

/** Baris toko dari /api/stores (STORE_SQL v1). */
export interface StoreRow {
  id: number;
  code: string;
  name: string;
  owner_name: string;
  phone: string;
  address: string;
  area: string;
  lat: number | null;
  lng: number | null;
  sales_id: number | null;
  sales_name: string | null;
  term_days: number;
  credit_limit: number;
  active: number | boolean;
  piutang: number;
  last_order: string | null;
  has_login?: number;
}

/** Form tambah/ubah toko (modal). store = null → toko baru. onSaved menerima toko tersimpan. */
export function useStoreForm(): (store: Record<string, unknown> | null, onSaved?: (s: StoreSaved) => void) => void {
  const { open } = useModal();
  const confirmBox = useConfirm();
  const toast = useToast();
  const { meta, can } = useSession();
  return useCallback(
    (store, onSaved) => {
      const s = store as Partial<StoreRow> | null;
      // Termin, batas kredit, sales penanggung jawab & status aktif: hak toko.kredit.
      const mgr = can('toko.kredit');
      // Titik toko = acuan check-in kunjungan: tanpa hak toko.titik hanya mengisi bila belum ada (server menolak pemindahan).
      const pinLocked = !can('toko.titik') && s?.lat != null && s?.lng != null;
      const sales = meta.sales ?? [];
      open({
        title: s ? `Ubah ${s.name}` : 'Toko baru',
        body: (
          <>
            <div className="grid2">
              <label className="f span2">
                Nama toko
                <input autoComplete="off" className="inp" name="name" required maxLength={120} defaultValue={s?.name || ''} />
              </label>
              <label className="f">
                Pemilik
                <input autoComplete="off" className="inp" name="owner_name" maxLength={100} defaultValue={s?.owner_name || ''} />
              </label>
              <label className="f">
                Telepon / WA
                <input
                  autoComplete="off"
                  className="inp"
                  name="phone"
                  inputMode="tel"
                  maxLength={30}
                  defaultValue={s?.phone || ''}
                />
              </label>
              <label className="f span2">
                Alamat
                <input autoComplete="off" className="inp" name="address" maxLength={300} defaultValue={s?.address || ''} />
              </label>
              <ChoiceSelect kind="area" name="area" value={s?.area} empty="— pilih area —" label="Area / rute" />
              <label className="f">
                {'Kode '}
                <span className="hint">kosong = otomatis</span>
                <input autoComplete="off" className="inp" name="code" maxLength={30} defaultValue={s?.code || ''} />
              </label>
              <label className="f">
                Latitude
                <input
                  autoComplete="off"
                  className="inp"
                  name="lat"
                  inputMode="decimal"
                  readOnly={pinLocked}
                  defaultValue={String(s?.lat ?? '')}
                />
              </label>
              <label className="f">
                Longitude
                <input
                  autoComplete="off"
                  className="inp"
                  name="lng"
                  inputMode="decimal"
                  readOnly={pinLocked}
                  defaultValue={String(s?.lng ?? '')}
                />
              </label>
            </div>
            {pinLocked ? <div className="small muted">Titik lokasi toko hanya bisa dipindah oleh admin/owner.</div> : null}
            <button
              type="button"
              className={`btn btn-sm${pinLocked ? ' hidden' : ''}`}
              data-m="gps"
              onClick={async (e) => {
                const b = e.currentTarget;
                b.disabled = true;
                const p = await getPosition(15000);
                b.disabled = false;
                if (!p) return toast('Lokasi tidak tersedia. Aktifkan GPS & izinkan lokasi.', true);
                const f = b.form as HTMLFormElement;
                (f.elements.namedItem('lat') as HTMLInputElement).value = p.lat.toFixed(6);
                (f.elements.namedItem('lng') as HTMLInputElement).value = p.lng.toFixed(6);
                toast(`Lokasi diisi (±${Math.round(p.acc)} m).`);
              }}
            >
              <Icon n="gps" cls="i-sm" /> Pakai lokasi saya sekarang (sedang di toko)
            </button>
            {mgr ? (
              <div className="grid2">
                <label className="f">
                  Sales
                  <select className="inp" name="sales_id" defaultValue={String(s?.sales_id ?? '')}>
                    <option value="">—</option>
                    {sales.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="f">
                  Termin tempo (hari)
                  <input
                    autoComplete="off"
                    className="inp"
                    type="number"
                    min="0"
                    name="term_days"
                    defaultValue={String(s?.term_days ?? 0)}
                  />
                </label>
                <label className="f">
                  Batas kredit (Rp, 0 = tanpa batas)
                  <input
                    autoComplete="off"
                    className="inp"
                    type="number"
                    min="0"
                    name="credit_limit"
                    defaultValue={String(s?.credit_limit ?? 0)}
                  />
                </label>
                {s ? (
                  <label className="check">
                    <input type="checkbox" name="active" defaultChecked={!!s.active} />
                    {' Aktif'}
                  </label>
                ) : null}
              </div>
            ) : null}
          </>
        ),
        foot: (m) => (
          <>
            <button type="button" className="btn" data-m="close" onClick={m.close}>
              Batal
            </button>
            <button className="btn btn-primary" type="submit">
              Simpan
            </button>
          </>
        ),
        submit: async (f, m) => {
          const body: Record<string, unknown> = {
            name: fv(f, 'name'),
            owner_name: fv(f, 'owner_name'),
            phone: fv(f, 'phone'),
            address: fv(f, 'address'),
            area: fv(f, 'area'),
            code: fv(f, 'code'),
            lat: fv(f, 'lat'),
            lng: fv(f, 'lng'),
          };
          if (mgr) {
            Object.assign(body, {
              sales_id: fv(f, 'sales_id') || null,
              term_days: fv(f, 'term_days'),
              credit_limit: fv(f, 'credit_limit'),
              ...(s ? { active: (f.elements.namedItem('active') as HTMLInputElement).checked } : {}),
            });
          }
          if (s?.active && body.active === false) {
            const ok = await confirmBox(
              `Nonaktifkan ${s.name}?`,
              'Toko tidak muncul lagi di daftar kunjungan & SPO, dan akun tokonya tidak bisa memesan. Riwayat tetap tersimpan.',
              { ok: 'Nonaktifkan', danger: true },
            );
            if (ok !== true) return;
          }
          const r = await api<{ item: StoreSaved }>(s ? 'PUT' : 'POST', s ? `/api/stores/${s.id}` : '/api/stores', body);
          m.close();
          toast('Toko disimpan.');
          onSaved?.(r.item);
        },
      });
    },
    [open, toast, meta, can, confirmBox],
  );
}
